import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  BarChart3, Trophy, Coins, TrendingUp, MessageSquare, Users, Star, Radio,
  Search, Bot, Trash2, Plus, Zap, Reply, Loader2, Check, Gift, CircleDot,
  Video, Clock, Eye, PlayCircle, AlertCircle, DollarSign, ExternalLink, ShieldCheck, Bell,
  Leaf,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import ConvertCommissionCard from "@/components/ConvertCommissionCard";
import PayoutsPanel from "@/components/PayoutsPanel";
import { BecomeCreatorButton, CreatorModeSettingsCard } from "@/components/CreatorModeToggle";
import CreatorWelcomeTour from "@/components/CreatorWelcomeTour";
import CashOutPanel from "@/components/CashOutPanel";
import BambooPanel from "@/components/BambooPanel";
import TopBadgeGoalCard from "@/components/top-badge/TopBadgeGoalCard";
import { Banknote, Crown } from "lucide-react";
import VipTiersStrip from "@/components/vip/VipTiersStrip";
import { useOutletContext } from "react-router-dom";

function Card({ label, value, hint, Icon }) {
  return (
    <div className="card-surface p-4">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        {Icon && <Icon size={14} className="text-rose-500" />}
      </div>
      <div className="font-display text-2xl">{value}</div>
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function AnalyticsTab({ data }) {
  const [followCount, setFollowCount] = useState(null);
  // Ref mirrors `followCount` so the WS onclose handler can consult the LATEST
  // value instead of the stale null captured when the effect first ran.
  // Without this, the REST fallback would re-fire even after the WS already
  // delivered a subscriber count (Stage 2 slice regression seen in review).
  const followCountRef = useRef(null);

  useEffect(() => {
    // Prefer the real-time WebSocket; fall back to REST if the handshake
    // fails (older proxies / restricted networks). See STAGE_2_ARCHITECTURE.md.
    let ws;
    let cancelled = false;
    const applyFollowCount = (val) => {
      followCountRef.current = val;
      setFollowCount(val);
    };
    const restFallback = () => {
      api.get("/me/live-follow-count")
        .then(({ data }) => { if (!cancelled) applyFollowCount(data); })
        .catch(() => { if (!cancelled) applyFollowCount(null); });
    };
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const wsUrl = backend.replace(/^http/, "ws") + "/api/ws/live-follow-count";
      ws = new WebSocket(wsUrl);
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (!cancelled && msg && typeof msg.subscriber_count === "number") {
            applyFollowCount(msg);
          }
        } catch { /* ignore */ }
      };
      ws.onerror = () => restFallback();
      ws.onclose = (e) => {
        // 4401 = unauthenticated → don't try REST either (would also fail).
        // Reading via the ref ensures we only fall back when the WS closed
        // BEFORE delivering any subscriber count, not after a stale null capture.
        if (e.code !== 4401 && followCountRef.current === null) restFallback();
      };
    } catch {
      restFallback();
    }
    return () => {
      cancelled = true;
      try { ws && ws.close(); } catch { /* ignore */ }
    };
  }, []);

  if (!data) return <div className="text-zinc-500 text-sm">Loading…</div>;
  const e = data.earnings;
  const max = Math.max(1, ...(data.gift_breakdown || []).map((b) => b.count));
  return (
    <div className="space-y-6" data-testid="creator-analytics">
      <ConvertCommissionCard />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Lifetime coins" value={e.total_coins.toLocaleString()} hint={`${e.total_gifts} gifts total`} Icon={Coins} />
        <Card label="Last 24h" value={e.day.coins.toLocaleString()} hint={`${e.day.gifts} gifts`} Icon={TrendingUp} />
        <Card label="Last 7d" value={e.week.coins.toLocaleString()} hint={`${e.week.gifts} gifts`} Icon={Zap} />
        <Card label="Last 30d" value={e.month.coins.toLocaleString()} hint={`${e.month.gifts} gifts`} Icon={BarChart3} />
      </div>

      {followCount && (
        <div
          data-testid="creator-live-follow-card"
          className="card-surface p-5 border-l-4 border-rose-500"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-rose-500 mb-2">
                <Bell size={13} /> Live Follow bell subscribers
              </div>
              <div className="font-display text-4xl leading-tight" data-testid="creator-live-follow-count">
                {followCount.subscriber_count.toLocaleString()}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">
                {followCount.subscriber_count === 0
                  ? "Nobody's tapped 🔔 yet — go live, get gifted, get followed."
                  : `${followCount.subscriber_count.toLocaleString()} ${followCount.subscriber_count === 1 ? "person will" : "people will"} be pinged the moment you hit Go Live.`}
              </div>
            </div>
            {followCount.new_this_week > 0 && (
              <div className="text-right">
                <div
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-400 border border-emerald-500/40 px-2 py-1"
                  data-testid="creator-live-follow-weekly"
                >
                  +{followCount.new_this_week} this week
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card-surface p-4">
        <div className="text-xs uppercase tracking-widest text-rose-500 mb-2 flex items-center gap-2">
          <Trophy size={12} /> Weekly ranking vs peers
        </div>
        <div className="flex items-baseline gap-2 mb-2">
          <span className="font-display text-4xl">{data.percentile_week || 0}</span>
          <span className="text-zinc-400">th percentile</span>
        </div>
        <div className="h-2 bg-black/40 border border-white/10">
          <div className="h-full bg-gradient-to-r from-rose-600 to-amber-400" style={{ width: `${data.percentile_week || 0}%` }} />
        </div>
        <div className="text-[11px] text-zinc-500 mt-1">
          {data.percentile_week >= 80 ? "🔥 Top-tier — you're crushing it this week." :
           data.percentile_week >= 50 ? "You're outperforming most creators this week." :
           "Keep posting + going live to climb the ranks."}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card-surface p-4">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-2">
            <Star size={12} /> Top fans (lifetime)
          </div>
          <div className="space-y-2" data-testid="top-fans-list">
            {(data.top_fans || []).slice(0, 10).map((f, idx) => (
              <div key={f.id} className="flex items-center gap-3">
                <div className="w-5 text-zinc-500 text-xs text-right">#{idx + 1}</div>
                <Avatar className="w-8 h-8 border border-white/10">
                  <AvatarImage src={f.avatar_url} />
                  <AvatarFallback className="bg-rose-600 text-xs">{f.display_name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <Link to={`/u/${f.id}`} className="text-sm hover:underline flex-1 truncate">
                  {f.display_name}
                </Link>
                <span className="text-amber-400 text-sm">{f.coins.toLocaleString()} 🪙</span>
              </div>
            ))}
            {(!data.top_fans || data.top_fans.length === 0) && (
              <div className="text-xs text-zinc-500">No fans yet — post + go live to earn your first gift.</div>
            )}
          </div>
        </div>

        <div className="card-surface p-4">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-2">
            <BarChart3 size={12} /> Gift breakdown
          </div>
          <div className="space-y-2" data-testid="gift-breakdown">
            {(data.gift_breakdown || []).map((b) => (
              <div key={b.key} className="flex items-center gap-2 text-sm">
                <span className="text-xl w-8">{b.emoji}</span>
                <span className="text-zinc-300 w-16 text-xs">{b.label}</span>
                <div className="flex-1 h-2 bg-black/40 border border-white/10">
                  <div className="h-full bg-rose-500" style={{ width: `${(b.count / max) * 100}%` }} />
                </div>
                <span className="text-zinc-400 text-xs w-10 text-right">×{b.count}</span>
                <span className="text-amber-400 text-xs w-14 text-right">{b.coins.toLocaleString()}c</span>
              </div>
            ))}
            {(!data.gift_breakdown || data.gift_breakdown.length === 0) && (
              <div className="text-xs text-zinc-500">No gifts received yet.</div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Streams hosted" value={data.live.streams_hosted} hint={data.live.live_now > 0 ? "🔴 Live now" : ""} Icon={Radio} />
        <Card label="Lifetime viewers" value={data.live.total_lifetime_viewers.toLocaleString()} Icon={Users} />
        <Card label="Battles won" value={data.live.battle_wins} Icon={Trophy} />
        <Card label="Posts" value={data.posts.count} Icon={MessageSquare} />
      </div>

      {data.top_post && (
        <div className="card-surface p-4">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Top-earning post</div>
          <div className="text-sm text-zinc-300 mb-2">"{data.top_post.content}"</div>
          <div className="text-amber-400 text-sm">🪙 {data.top_post.gifts_count.toLocaleString()} coins in gifts</div>
        </div>
      )}
    </div>
  );
}

function InboxTab() {
  const [rows, setRows] = useState([]);
  const [sort, setSort] = useState("recent");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [vipOnly, setVipOnly] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort };
      if (unreadOnly) params.unread_only = true;
      if (vipOnly) params.vip_only = true;
      if (q) params.q = q;
      const { data } = await api.get("/creator/inbox", { params });
      setRows(data);
    } catch (e) {
      // Chart data is best-effort — don't block the dashboard if it fails.
      console.debug("[CreatorDashboard] chart fetch failed", e);
    } finally { setLoading(false); }
  }, [sort, unreadOnly, vipOnly, q]);

  useEffect(() => { load(); }, [load]);

  const bulkMark = async () => {
    try {
      const { data } = await api.post("/creator/inbox/mark-all-read");
      toast.success(`Marked ${data.marked} messages as read`);
      load();
    } catch (e) { toast.error("Bulk mark failed"); }
  };

  return (
    <div className="space-y-3" data-testid="creator-inbox">
      <div className="flex flex-wrap items-center gap-2">
        {["recent", "top_gifter", "unread"].map((s) => (
          <button
            key={s}
            onClick={() => setSort(s)}
            data-testid={`inbox-sort-${s}`}
            className={`text-xs uppercase tracking-widest px-3 py-2 border ${
              sort === s ? "bg-rose-600/20 border-rose-500 text-white" : "bg-black/30 border-white/10 text-zinc-400"
            }`}
          >
            {s === "recent" ? "Recent" : s === "top_gifter" ? "Top gifter" : "Unread first"}
          </button>
        ))}
        <button
          onClick={() => setUnreadOnly((v) => !v)}
          data-testid="inbox-filter-unread"
          className={`text-xs uppercase tracking-widest px-3 py-2 border ${unreadOnly ? "bg-amber-500/20 border-amber-400 text-amber-400" : "bg-black/30 border-white/10 text-zinc-400"}`}
        >
          Unread only
        </button>
        <button
          onClick={() => setVipOnly((v) => !v)}
          data-testid="inbox-filter-vip"
          className={`text-xs uppercase tracking-widest px-3 py-2 border ${vipOnly ? "bg-rose-600/20 border-rose-500 text-rose-400" : "bg-black/30 border-white/10 text-zinc-400"}`}
        >
          <Star size={12} className="inline mr-1" /> VIP only
        </button>
        <Button onClick={bulkMark} className="btn-crimson rounded-none ml-auto" data-testid="inbox-mark-all-read">
          <Check size={14} className="mr-1" /> Mark all read
        </Button>
      </div>

      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search inbox" className="bg-black/40 border-white/10 rounded-none pl-9 py-5" data-testid="inbox-search" />
      </div>

      {loading && <div className="text-xs text-zinc-500">Loading…</div>}

      <div className="space-y-2">
        {rows.map((r) => (
          <Link
            to={`/inbox?c=${r.id}`}
            key={r.id}
            className="flex items-center gap-3 card-surface p-3 hover-lift"
            data-testid={`inbox-row-${r.id}`}
          >
            <Avatar className="w-11 h-11 border border-white/10">
              <AvatarImage src={r.other?.avatar_url} />
              <AvatarFallback className="bg-rose-600 text-sm">{r.other?.display_name?.[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="text-sm font-medium truncate">{r.other?.display_name}</div>
                {r.is_vip && <span className="text-[10px] uppercase tracking-widest text-rose-400 border border-rose-500/40 px-1.5 py-0.5">VIP</span>}
              </div>
              <div className="text-[11px] text-zinc-500 truncate">{r.last_message_preview || "…"}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-xs text-amber-400">{r.fan_coins.toLocaleString()} 🪙</div>
              {r.unread > 0 && (
                <div className="mt-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-[10px] font-bold bg-rose-600 text-white rounded-full">
                  {r.unread}
                </div>
              )}
            </div>
          </Link>
        ))}
        {!loading && rows.length === 0 && (
          <div className="text-xs text-zinc-500 text-center py-6">No conversations match.</div>
        )}
      </div>
    </div>
  );
}

function TemplatesTab() {
  const [rows, setRows] = useState([]);
  const [label, setLabel] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/creator/reply-templates").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!label.trim() || !body.trim()) return toast.error("Both fields required");
    setBusy(true);
    try {
      await api.post("/creator/reply-templates", { label: label.trim(), body: body.trim() });
      setLabel(""); setBody("");
      toast.success("Template saved");
      load();
    } catch (e) { toast.error("Save failed"); } finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete template?")) return;
    await api.delete(`/creator/reply-templates/${id}`);
    load();
  };
  const copyBody = async (t) => {
    try { await navigator.clipboard.writeText(t.body); toast.success("Template copied — paste into any DM"); }
    catch (e) { toast.error("Copy failed"); }
  };

  return (
    <div className="space-y-4" data-testid="creator-templates">
      <div className="card-surface p-4 space-y-3">
        <div className="text-xs uppercase tracking-widest text-zinc-500">Add a new template</div>
        <Input value={label} onChange={(e) => setLabel(e.target.value.slice(0, 40))} placeholder="Label (e.g., 'Thanks for tip')" className="bg-black/40 border-white/10 rounded-none py-5" data-testid="template-label" />
        <Textarea value={body} onChange={(e) => setBody(e.target.value.slice(0, 1000))} placeholder='Body — you can use {name} which gets replaced with the fan handle when you paste. e.g., "Thanks so much {name} 🌹 stay tuned"' rows={3} className="bg-black/40 border-white/10 rounded-none focus-visible:ring-rose-600" data-testid="template-body" />
        <div className="flex justify-end">
          <Button onClick={add} disabled={busy} className="btn-crimson rounded-none px-5 py-5" data-testid="template-add">
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />} Save template
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {rows.map((t) => (
          <div key={t.id} className="card-surface p-4" data-testid={`template-${t.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium">{t.label}</div>
              <div className="flex items-center gap-2">
                <Button onClick={() => copyBody(t)} className="rounded-none bg-white/10 hover:bg-white/15 text-xs px-3 py-2" data-testid={`template-copy-${t.id}`}>
                  <Reply size={12} className="mr-1" /> Copy
                </Button>
                <Button onClick={() => del(t.id)} className="rounded-none bg-rose-600/20 hover:bg-rose-600/30 text-rose-400 text-xs px-3 py-2" data-testid={`template-delete-${t.id}`}>
                  <Trash2 size={12} />
                </Button>
              </div>
            </div>
            <div className="text-xs text-zinc-400 whitespace-pre-wrap">{t.body}</div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="text-xs text-zinc-500 text-center py-6">No templates yet — add a "Thanks!" one above.</div>
        )}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [enabled, setEnabled] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/creator/auto-reply").then(({ data }) => {
      setEnabled(!!data.enabled);
      setMsg(data.message || "");
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/creator/auto-reply", { enabled, message: msg });
      toast.success(enabled ? "Auto-reply enabled" : "Auto-reply saved");
    } catch (e) { toast.error("Save failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="creator-settings">
      <div className="card-surface p-5 space-y-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-rose-500 mb-1 flex items-center gap-2">
            <Bot size={12} /> Auto-reply when offline
          </div>
          <div className="text-sm text-zinc-500">
            Sends a canned DM automatically to first-time messages and to viewers who
            send you a gift during a live stream. Toggle on/off, edit anytime.
          </div>
          <div className="text-[11px] text-zinc-500 mt-2">
            Template variables:{" "}
            <code className="text-amber-400">{"{sender}"}</code>{", "}
            <code className="text-amber-400">{"{gift}"}</code>{", "}
            <code className="text-amber-400">{"{coins}"}</code>
          </div>
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-5 h-5 accent-rose-500" data-testid="auto-reply-toggle" />
          <span className="text-sm">{enabled ? "Auto-reply is ON" : "Auto-reply is OFF"}</span>
        </label>
        <Textarea value={msg} onChange={(e) => setMsg(e.target.value.slice(0, 400))} placeholder="e.g., Thanks {sender} for the {gift}! You made my night 🌸" rows={3} className="bg-black/40 border-white/10 rounded-none" data-testid="auto-reply-message" />
        <div className="flex justify-end">
          <Button onClick={save} disabled={busy} className="btn-crimson rounded-none px-5 py-5" data-testid="auto-reply-save">
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function PrizeRow({ prize, onDone, onReopen }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const isDone = prize.status === "done";
  const when = new Date(prize.created_at);

  const complete = async () => {
    setBusy(true);
    try {
      await onDone(prize.id, note.trim() || null);
      setNote("");
    } finally { setBusy(false); }
  };
  const reopen = async () => {
    setBusy(true);
    try { await onReopen(prize.id); } finally { setBusy(false); }
  };

  return (
    <div
      className={`card-surface p-4 ${isDone ? "opacity-70" : ""}`}
      data-testid={`prize-row-${prize.id}`}
    >
      <div className="flex items-start gap-3">
        <Avatar className="w-10 h-10 border border-white/10">
          <AvatarImage src={prize.sender_avatar_url} />
          <AvatarFallback className="bg-rose-600 text-white text-xs">
            {(prize.sender_display_name || "P")[0]?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-semibold text-white truncate">{prize.sender_display_name || "Panda Member"}</span>
            <span className="text-[10px] uppercase tracking-widest bg-yellow-400/15 text-yellow-300 px-1.5 py-0.5 rounded-sm">
              {prize.coins} coins
            </span>
            {isDone && (
              <span className="text-[10px] uppercase tracking-widest bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded-sm">
                Done
              </span>
            )}
          </div>
          <div className="text-sm">
            Won <span className="text-yellow-300 font-semibold">{prize.segment}</span>
            <span className="text-zinc-500"> · {prize.stream_title || "live stream"}</span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">
            {when.toLocaleString()}
            {isDone && prize.note && ` · note: ${prize.note}`}
          </div>

          {!isDone ? (
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 300))}
                placeholder="Optional note DM'd to the winner (e.g., link, ETA)…"
                className="bg-black/40 border-white/10 rounded-none flex-1"
                data-testid={`prize-note-${prize.id}`}
              />
              <Button
                onClick={complete}
                disabled={busy}
                className="btn-crimson rounded-none whitespace-nowrap"
                data-testid={`prize-done-${prize.id}`}
              >
                {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}
                Mark done
              </Button>
            </div>
          ) : (
            <div className="mt-2">
              <button
                onClick={reopen}
                disabled={busy}
                data-testid={`prize-reopen-${prize.id}`}
                className="text-[11px] uppercase tracking-widest text-rose-400 hover:text-rose-300"
              >
                Reopen
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PrizesTab() {
  const [tab, setTab] = useState("pending"); // "pending" | "done"
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/creator/prizes", { params: { status_filter: tab } });
      setRows(data || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const markDone = async (id, note) => {
    await api.post(`/creator/prizes/${id}/done`, { note });
    toast.success("Prize marked done — winner has been DM'd.");
    load();
  };
  const reopen = async (id) => {
    await api.post(`/creator/prizes/${id}/reopen`);
    toast.success("Prize reopened.");
    load();
  };

  const pendingCount = tab === "pending" ? rows.length : null;

  return (
    <div className="space-y-4" data-testid="creator-prizes">
      <div className="flex items-center gap-2 border-b border-white/10 pb-2">
        {["pending", "done"].map((t) => (
          <button
            key={t}
            data-testid={`prizes-filter-${t}`}
            onClick={() => setTab(t)}
            className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border ${
              tab === t
                ? "bg-rose-600 border-rose-600 text-white"
                : "border-white/10 text-zinc-400 hover:text-white"
            }`}
          >
            {t === "pending" ? "Pending" : "Completed"}
            {t === "pending" && pendingCount !== null && pendingCount > 0 && (
              <span className="ml-1.5 text-[10px] bg-white/20 rounded-full px-1.5">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Loading prizes…</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-14 text-zinc-500">
          <CircleDot size={40} className="mx-auto mb-3 opacity-40" />
          <div className="text-sm">
            {tab === "pending"
              ? "No prizes waiting on you — configure a wheel next time you go live to start collecting."
              : "No completed prizes yet."}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((p) => (
            <PrizeRow key={p.id} prize={p} onDone={markDone} onReopen={reopen} />
          ))}
        </div>
      )}
    </div>
  );
}

function fmtDuration(seconds) {
  if (!seconds || seconds <= 0) return "0s";
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rem = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${rem}s`;
  return `${rem}s`;
}

function VideosTab() {
  const [timeframe, setTimeframe] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError("");
    api.get("/creator/mux-metrics", { params: { timeframe } })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => { if (!cancelled) { setError(e?.response?.data?.detail || "Could not load Mux analytics."); setData(null); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [timeframe]);

  const timeframes = [
    { v: "24h", label: "24h" },
    { v: "7d",  label: "7d" },
    { v: "30d", label: "30d" },
    { v: "90d", label: "90d" },
  ];

  return (
    <div className="space-y-6" data-testid="creator-videos">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-rose-500 mb-1 flex items-center gap-2">
            <Video size={12} /> Video engagement · powered by Mux Data
          </div>
          <div className="text-sm text-zinc-500">
            Views, unique viewers and watch time across your reels and live streams.
          </div>
        </div>
        <div className="flex items-center gap-1 bg-black/30 border border-white/10 rounded-none p-1">
          {timeframes.map((t) => (
            <button
              key={t.v}
              onClick={() => setTimeframe(t.v)}
              data-testid={`mux-timeframe-${t.v}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest ${
                timeframe === t.v ? "bg-rose-600 text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="card-surface p-8 text-center text-zinc-500 text-sm">
          <Loader2 size={20} className="mx-auto mb-2 animate-spin" /> Fetching Mux Data…
        </div>
      )}

      {!loading && error && (
        <div className="card-surface p-6 border-rose-500/30" data-testid="mux-metrics-error">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-rose-500 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-white mb-1">Couldn't load Mux Data</div>
              <div className="text-xs text-zinc-400">{error}</div>
            </div>
          </div>
        </div>
      )}

      {!loading && data && !data.configured && (
        <div className="card-surface p-6" data-testid="mux-not-configured">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-white mb-1">Mux Data not connected yet</div>
              <div className="text-xs text-zinc-400 mb-2">
                Add <code className="text-amber-300">MUX_TOKEN_ID</code> and{" "}
                <code className="text-amber-300">MUX_TOKEN_SECRET</code> in your backend env
                (Mux Dashboard → Settings → Access Tokens). Analytics will start flowing
                once tokens are set.
              </div>
              <div className="text-[11px] text-zinc-500">
                Your player-side tracking is already live — Mux is collecting views right
                now. This tab will populate the moment tokens are added.
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && data?.configured && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card
              label="Total views"
              value={(data.totals.views || 0).toLocaleString()}
              hint={data.timeframe_label}
              Icon={Eye}
            />
            <Card
              label="Unique viewers"
              value={(data.totals.unique_viewers || 0).toLocaleString()}
              hint={data.timeframe_label}
              Icon={Users}
            />
            <Card
              label="Watch time"
              value={fmtDuration(data.totals.watch_time_seconds)}
              hint={`${data.totals.watch_time_hours}h total`}
              Icon={Clock}
            />
            <Card
              label="Playback quality"
              value={`${(data.totals.playback_success_score || 0).toFixed(1)}%`}
              hint={`${Math.round(data.totals.avg_startup_ms || 0)}ms startup`}
              Icon={PlayCircle}
            />
          </div>

          <div className="card-surface p-4">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-2">
              <BarChart3 size={12} /> Top videos this period
            </div>
            {(data.top_videos || []).length === 0 ? (
              <div className="text-xs text-zinc-500 py-4 text-center">
                No plays yet in this timeframe. Post a reel or go live — data appears within ~60s of the first play.
              </div>
            ) : (
              <div className="space-y-2" data-testid="mux-top-videos">
                {data.top_videos.map((v, idx) => {
                  const maxViews = Math.max(1, ...data.top_videos.map((x) => x.views));
                  return (
                    <div key={`${v.title}-${idx}`} className="flex items-center gap-3 text-sm">
                      <div className="w-5 text-zinc-500 text-xs text-right">#{idx + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white truncate text-sm">{v.title}</div>
                        <div className="h-1.5 bg-black/40 border border-white/10 mt-1">
                          <div className="h-full bg-gradient-to-r from-rose-600 to-amber-400" style={{ width: `${(v.views / maxViews) * 100}%` }} />
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-white text-sm">{v.views.toLocaleString()} views</div>
                        <div className="text-[11px] text-zinc-500">{fmtDuration(v.watch_time_seconds)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {(data.views_timeseries || []).length > 1 && (
            <div className="card-surface p-4">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-2">
                <TrendingUp size={12} /> Views trend
              </div>
              <div className="flex items-end gap-1 h-24" data-testid="mux-timeseries">
                {(() => {
                  const rows = data.views_timeseries;
                  const max = Math.max(1, ...rows.map((r) => r.views));
                  return rows.map((r, i) => (
                    <div
                      key={r.t || i}
                      title={new Date(r.t).toLocaleDateString() + " — " + r.views + " views"}
                      className="flex-1 bg-rose-600/70 hover:bg-rose-500 transition-colors min-w-[3px]"
                      style={{ height: `${Math.max(2, (r.views / max) * 100)}%` }}
                    />
                  ));
                })()}
              </div>
              <div className="flex justify-between text-[10px] text-zinc-500 mt-2">
                <span>{new Date(data.views_timeseries[0].t).toLocaleDateString()}</span>
                <span>{new Date(data.views_timeseries[data.views_timeseries.length - 1].t).toLocaleDateString()}</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function CreatorDashboardPage() {
  const { user } = useAuth();
  const { openGoLive } = useOutletContext() || {};
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("analytics");

  useEffect(() => {
    api.get("/creator/summary").then(({ data }) => setSummary(data)).catch(() => setSummary(null));
  }, []);
  if (!user) return <Navigate to="/login" replace />;
  if (!(user.is_creator || user.role === "admin")) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center">
        <div className="text-6xl mb-3">🎯</div>
        <h1 className="font-display text-3xl mb-2">Creator Dashboard</h1>
        <p className="text-sm text-zinc-500">
          Creator mode unlocks Go Live, analytics, your inbox tools, videos and the prize wheel. It&apos;s your choice — turn it on whenever you&apos;re ready, and off whenever you like.
        </p>
        <div className="mt-6"><BecomeCreatorButton variant="cta" /></div>
        <p className="text-sm text-zinc-400 mt-4">
          Looking to get paid for tips and gifts? Every eligible member can set up payouts — no creator status required.{" "}
          <Link to="/earnings" className="text-rose-500 hover:underline" data-testid="creator-fallback-earnings-link">Open Earnings &amp; Payouts</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="creator-dashboard">
      <CreatorWelcomeTour userId={user.id} onGoLive={openGoLive} onTab={setTab} />
      <header className="mb-6">
        <div className="text-xs uppercase tracking-[0.3em] text-rose-500 mb-2">🎯 Creator</div>
        <h1 className="font-display text-3xl md:text-4xl">Your creator dashboard</h1>
        <p className="text-sm text-zinc-500 mt-1">Earnings, top fans, inbox management — everything to run your My Panda X presence.</p>
      </header>
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-transparent border-b border-white/10 rounded-none w-full justify-start gap-2 p-0 h-auto overflow-x-auto flex-nowrap">
          {[
            { v: "analytics", label: "Analytics", Icon: BarChart3 },
            { v: "videos", label: "Videos", Icon: Video },
            { v: "payouts", label: "Payouts", Icon: DollarSign },
            { v: "cashout", label: "Cash out", Icon: Banknote },
            { v: "bamboo", label: "Bamboo", Icon: Leaf },
            { v: "vip", label: "VIP badges", Icon: Crown },
            { v: "inbox", label: "Inbox", Icon: MessageSquare },
            { v: "prizes", label: "Prizes", Icon: Gift, badge: summary?.pending_prizes },
            { v: "templates", label: "Templates", Icon: Reply },
            { v: "settings", label: "Settings", Icon: Bot },
          ].map(({ v, label, Icon, badge }) => (
            <TabsTrigger
              key={v}
              value={v}
              data-testid={`creator-tab-${v}`}
              className="shrink-0 whitespace-nowrap rounded-none bg-transparent border-b-2 border-transparent data-[state=active]:border-rose-600 data-[state=active]:bg-transparent data-[state=active]:text-white text-zinc-400 px-4 py-3 relative"
            >
              <Icon size={14} className="mr-2" /> {label}
              {badge && badge > 0 ? (
                <span
                  className="ml-2 bg-rose-600 text-white text-[10px] rounded-full px-1.5 py-0.5 leading-none"
                  data-testid={`creator-tab-${v}-badge`}
                >
                  {badge}
                </span>
              ) : null}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="analytics" className="mt-6 space-y-6"><TopBadgeGoalCard /><AnalyticsTab data={summary} /></TabsContent>
        <TabsContent value="videos" className="mt-6"><VideosTab /></TabsContent>
        <TabsContent value="payouts" className="mt-6"><PayoutsPanel /></TabsContent>
        <TabsContent value="cashout" className="mt-6"><CashOutPanel /></TabsContent>
        <TabsContent value="bamboo" className="mt-6"><BambooPanel /></TabsContent>
        <TabsContent value="vip" className="mt-6 space-y-4" data-testid="creator-vip-tab">
          <div className="max-w-2xl">
            <h2 className="text-base md:text-lg font-medium text-white">Your supporters earn monthly VIP badges</h2>
            <p className="text-sm text-zinc-400 mt-1">Fans unlock Green → Purple → Black Panda VIP and Panda Boss by purchasing Panda Coins each month (gifts are tracked separately). Badges reset on the 1st — and it never changes your earnings, payouts or the 45/55 split.</p>
          </div>
          <VipTiersStrip context="creator" className="max-w-2xl" />
        </TabsContent>
        <TabsContent value="inbox" className="mt-6"><InboxTab /></TabsContent>
        <TabsContent value="prizes" className="mt-6"><PrizesTab /></TabsContent>
        <TabsContent value="templates" className="mt-6"><TemplatesTab /></TabsContent>
        <TabsContent value="settings" className="mt-6"><SettingsTab /><CreatorModeSettingsCard /></TabsContent>
      </Tabs>
    </div>
  );
}
